import {
  reportingPrisma as prisma,
  BookableItemConfigSource,
  OrderItemStatus,
} from "client";
import { buildPaymentSettlementExpressions } from "services/virtualOrderStatus";
import { soldCompletedOrderItemWhere } from "./countingRules";

type EntityCompletedRow = { count?: number };

/**
 * Entity registration completed count — starts from Order by eventId (indexed)
 * so we only touch sold items, not every seat on the floor plan.
 */
export const getCompletedEntityOrderItemsCount = async (eventId: string) => {
  const raw = await prisma.order.aggregateRaw({
    pipeline: [
      { $match: { eventId: { $oid: eventId } } },
      { $project: { _id: 1, total: 1 } },
      {
        $lookup: {
          from: "OrderItem",
          let: { orderId: "$_id", orderTotal: "$total" },
          pipeline: [
            {
              $match: {
                $expr: { $eq: ["$orderId", "$$orderId"] },
                status: OrderItemStatus.CONFIRMED,
                entityId: { $type: "objectId" },
              },
            },
            {
              $lookup: {
                from: "Entity",
                let: { eid: "$entityId" },
                pipeline: [
                  { $match: { $expr: { $eq: ["$_id", "$$eid"] } } },
                  {
                    $match: {
                      $or: [
                        { deletedAt: null },
                        { deletedAt: { $exists: false } },
                      ],
                    },
                  },
                  {
                    $lookup: {
                      from: "EntityTier",
                      localField: "entityTierId",
                      foreignField: "_id",
                      as: "tier",
                    },
                  },
                  {
                    $unwind: {
                      path: "$tier",
                      preserveNullAndEmptyArrays: true,
                    },
                  },
                  {
                    $lookup: {
                      from: "BookableItem",
                      let: {
                        biId: {
                          $cond: {
                            if: {
                              $eq: [
                                "$configSource",
                                BookableItemConfigSource.ENTITY,
                              ],
                            },
                            then: "$bookableItemId",
                            else: "$tier.bookableItemId",
                          },
                        },
                      },
                      pipeline: [
                        { $match: { $expr: { $eq: ["$_id", "$$biId"] } } },
                        { $project: { price: 1 } },
                      ],
                      as: "bookableItem",
                    },
                  },
                  {
                    $project: {
                      bookablePrice: {
                        $ifNull: [
                          { $arrayElemAt: ["$bookableItem.price", 0] },
                          0,
                        ],
                      },
                    },
                  },
                ],
                as: "entity",
              },
            },
            { $unwind: "$entity" },
            {
              $lookup: {
                from: "Payment",
                let: { oid: "$$orderId" },
                pipeline: [
                  { $match: { $expr: { $eq: ["$orderId", "$$oid"] } } },
                  { $project: { status: 1 } },
                ],
                as: "payments",
              },
            },
            {
              $addFields: {
                bookablePrice: "$entity.bookablePrice",
                orderTotal: "$$orderTotal",
                paymentStatuses: {
                  $map: {
                    input: "$payments",
                    as: "p",
                    in: "$$p.status",
                  },
                },
              },
            },
            {
              $addFields: {
                isFreeTicket: {
                  $cond: {
                    if: { $ne: ["$total", null] },
                    then: { $lte: ["$total", 0] },
                    else: { $lte: ["$bookablePrice", 0] },
                  },
                },
                isPaidTicket: {
                  $cond: {
                    if: { $ne: ["$total", null] },
                    then: { $gt: ["$total", 0] },
                    else: { $gt: ["$bookablePrice", 0] },
                  },
                },
                isFreeViaOrder: {
                  $and: [
                    {
                      $or: [
                        { $eq: ["$total", null] },
                        { $eq: [{ $type: "$total" }, "missing"] },
                      ],
                    },
                    {
                      $or: [
                        { $eq: ["$orderTotal", 0] },
                        { $eq: ["$orderTotal", null] },
                        { $eq: [{ $type: "$orderTotal" }, "missing"] },
                      ],
                    },
                  ],
                },
                ...buildPaymentSettlementExpressions("$paymentStatuses"),
              },
            },
            {
              $match: {
                $or: [
                  { isPaidTicket: true, anyPaid: true },
                  { isFreeTicket: true },
                  { isFreeViaOrder: true, anyPaid: false },
                ],
              },
            },
            { $project: { _id: 1 } },
          ],
          as: "completedItems",
        },
      },
      { $unwind: "$completedItems" },
      { $count: "count" },
    ],
  });

  const row = (Array.isArray(raw) ? raw[0] : null) as
    | EntityCompletedRow
    | null
    | undefined;
  return Number(row?.count ?? 0);
};

export const getCompletedOrderItemsCount = async (eventId: string) => {
  return prisma.orderItem.count({
    where: soldCompletedOrderItemWhere(eventId),
  });
};
